import clsx from 'clsx'
import {FormikProps, getIn} from 'formik'
import {cloneDeep, find, isEmpty, isEqual} from 'lodash'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  DIMENSION_UNIT,
  DS_SEPARATOR,
  INVALID_COLOR,
  VALID_COLOR,
  WEIGHT_UNIT,
} from '../../../../../_gori/constants'
import {KTSVG} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {Button} from '../../../../../_gori/partials/widgets'
import {CreatePackagesModal} from '../../../settings'
import {CARRIER_PACKAGE_TYPE, OPTION_PACKAGE_TYPE} from '../../core/_const'

type Props = {
  data: any
  textLabel?: string
  labelClassName?: any
  required?: boolean
  formik: FormikProps<any>
  name: string
  placeholder?: string
  className?: any
  showAddPackage?: boolean
  disabled?: boolean
  labelPackageType?: string
  setLabelPackageType?: React.Dispatch<React.SetStateAction<string | undefined>>
  oneRateUnmatched?: boolean
  onResetOneRateUnmatched?: () => void
}

export type ParcelItem = {
  id: number
  package_id: number | null
  package_type: string | null
  length: number
  width: number
  height: number
  weight: number
  dimension_unit: string
  weight_unit: string
  package_count?: number
  editable: boolean
  hidden: string
}

const PackageType: React.FC<Props> = ({
  data,
  textLabel,
  labelClassName,
  required,
  formik,
  name,
  placeholder = '',
  className,
  showAddPackage = true,
  disabled = false,
  labelPackageType,
  setLabelPackageType = () => {},
  oneRateUnmatched = false,
  onResetOneRateUnmatched = () => {},
}) => {
  const storeRef = useRef<any>(null)
  const intl = useIntl()
  const {getPackages} = useStatesGlobal()
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [visibleSubMenu, setVisibleSubMenu] = useState<string | undefined>(undefined)
  const [showModalNewPackage, setShowModalNewPackage] = useState<boolean>(false)
  const [isHighlighted, setIsHighlighted] = useState(false)
  const prevLabelRef = useRef<string | undefined>(labelPackageType)

  const firstParcel = Object.values(formik.values.parcels)?.[0] as ParcelItem
  const showInputDimension = !!data.find(
    (option) =>
      isEqual(option, OPTION_PACKAGE_TYPE.INPUT_DIMENSION) &&
      (firstParcel?.height || firstParcel?.length || firstParcel?.width)
  )

  const prevParcelsRef = useRef<ParcelItem | null>(null)
  const prevUnitRef = useRef(formik.values?.parcels?.[0]?.dimension_unit)

  let formikErrors = getIn(formik.errors, name)
  let formikTouched = getIn(formik.touched, name)
  let isInvalid = formikTouched && formikErrors
  const hasSelectedPackageType = !isEmpty(labelPackageType) && labelPackageType !== placeholder
  const isValid = formikTouched && !formikErrors && hasSelectedPackageType
  const borderColor = isValid ? VALID_COLOR : isInvalid ? INVALID_COLOR : DS_SEPARATOR

  const customStyle: React.CSSProperties = {
    borderColor,
    borderWidth: '1px',
    borderStyle: 'solid',
    backgroundColor: isHighlighted ? '#f0fdfa' : (disabled && '#eff2f5') || '#ffffff',
    transition: 'background-color 0.4s ease',
    color: Object.keys(formik.getFieldProps('parcels').value).length > 1 ? '#94A3B8' : '#5e6278',
  }

  const handleCreatePackagesSuccess = async (values) => {
    await getPackages()

    const [firstParcelKey, firstParcel] = Object.entries(formik.values.parcels)?.[0] as [
      firstParcelKey: string,
      firstParcel: ParcelItem
    ]

    await formik.setFieldValue(`parcels.${firstParcelKey}`, {
      ...firstParcel,
      ...values,
      package_type: '',
    })
  }

  const handleDropdownClick = () => {
    setVisibleMenu(!visibleMenu)
  }

  const openChoose = useCallback(() => {
    if (oneRateUnmatched) return
    let found = false
    let parcelsCurrent = cloneDeep(Object.values(formik.values.parcels)?.[0]) as ParcelItem

    // Handle Input Dimension case first
    if (!parcelsCurrent?.package_type && !parcelsCurrent?.package_id) {
      if (showInputDimension) {
        setLabelPackageType(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
        setVisibleSubMenu(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
        return
      } else {
        setLabelPackageType(placeholder)
        return
      }
    }

    // Check in data for matching package
    data?.forEach((item) => {
      let {label, options} = item
      if (!options && label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) return

      if (!options) {
        options = [item]
      }

      options.forEach((subItem) => {
        if (found) return
        const {
          value: {package_type, package_id, length, width, height, dimension_unit},
        } = subItem

        const checkEqual =
          Number(length) === Number(parcelsCurrent.length) &&
          Number(width) === Number(parcelsCurrent.width) &&
          Number(height) === Number(parcelsCurrent.height) &&
          dimension_unit === parcelsCurrent.dimension_unit

        // For custom package, check by package_id
        if (
          label === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label &&
          package_id === parcelsCurrent?.package_id &&
          checkEqual
        ) {
          found = true
          setVisibleSubMenu(label)
          setLabelPackageType(subItem.label)
          return
        }
        // For carrier packages, check by package_type
        const carrierLabels = CARRIER_PACKAGE_TYPE.map((item) => item.label)
        if (package_type === parcelsCurrent?.package_type && carrierLabels.includes(label)) {
          found = true
          setVisibleSubMenu(label)
          setLabelPackageType(subItem.label)
          return
        }
      })
    })
    prevParcelsRef.current = parcelsCurrent
    prevUnitRef.current = parcelsCurrent.dimension_unit
    // If no match found and not already set to Input Dimension
    if (!found && labelPackageType !== OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) {
      setLabelPackageType(
        showInputDimension ? OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label : placeholder
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    formik.values?.parcels,
    labelPackageType,
    oneRateUnmatched,
    placeholder,
    setLabelPackageType,
    showInputDimension,
  ])

  useEffect(() => {
    const parcels = formik.values?.parcels || {}
    const firstParcel: any = Object.values(parcels)?.[0]
    const prevParcels = prevParcelsRef.current

    // If the parcel has changed (compared to the previous one), call openChoose again
    if (!isEqual(prevParcels, firstParcel)) {
      openChoose()
      prevParcelsRef.current = firstParcel
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.parcels])

  // Trigger highlight when labelPackageType changes programmatically (e.g. from dimension changes)
  useEffect(() => {
    if (prevLabelRef.current !== undefined && labelPackageType !== prevLabelRef.current) {
      setIsHighlighted(true)
      const timer = setTimeout(() => setIsHighlighted(false), 800)
      return () => clearTimeout(timer)
    }
    prevLabelRef.current = labelPackageType
  }, [labelPackageType])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (storeRef.current && !storeRef.current.contains(event.target)) {
        setVisibleMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openChoose])

  const renderMenuItem = (item, index) => {
    const {label, options, value} = item

    const highLightPackageCarrier = (subItem) => {
      const parcelsCurrent = Object.values(formik.values.parcels)?.[0] as ParcelItem
      if (!parcelsCurrent) return false
      const {
        value: {package_type, package_id},
      } = subItem

      const checkEqual = options.filter((item) => {
        return (
          item.value.length === Number(parcelsCurrent?.length) &&
          item.value.width === Number(parcelsCurrent?.width) &&
          item.value.height === Number(parcelsCurrent?.height) &&
          item.value.dimension_unit === parcelsCurrent?.dimension_unit
        )
      })
      const itemFound = find(checkEqual, {label: subItem.label})
      if (
        package_type === parcelsCurrent?.package_type &&
        package_id === parcelsCurrent?.package_id &&
        itemFound
      ) {
        return true
      } else {
        return false
      }
    }

    const handleClickPackage = async (item, options) => {
      onResetOneRateUnmatched()
      const currentParcels = formik.values.parcels || {}
      const [firstParcelKey, firstParcel] = Object.entries(currentParcels)?.[0] as [
        firstParcelKey: string,
        firstParcel: ParcelItem
      ]
      if (label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) {
        setVisibleMenu(false)
        setVisibleSubMenu(label)

        await formik.setFieldValue('parcels', {
          [firstParcelKey]: {
            length: '',
            width: '',
            height: '',
            package_id: null,
            package_type: '',
            package_count: firstParcel.package_count || 1,
            weight: firstParcel.weight,
            dimension_unit: DIMENSION_UNIT.IN,
            weight_unit: firstParcel.weight_unit ?? WEIGHT_UNIT.OZ,
          },
        })
        await formik.setFieldTouched(name, true)
        setLabelPackageType(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
      } else {
        // Case USPS Cubic
        if (isEmpty(options) && label !== OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label) {
          await formik.setFieldValue('parcels', {
            [firstParcelKey]: {
              ...currentParcels[firstParcelKey],
              ...item.value,
              package_count: 1,
            },
          })
          await formik.setFieldTouched(name, true)
          setVisibleMenu(false)
        } else {
          setVisibleSubMenu((prev) => (prev === label ? undefined : label))
        }
      }
    }

    const handleClickPackageChild = async (subItem) => {
      onResetOneRateUnmatched()
      setVisibleMenu(false)
      const currentParcels = formik.values.parcels || {}
      const [firstParcelKey, firstParcel] = Object.entries(currentParcels)?.[0] as [
        firstParcelKey: string,
        firstParcel: ParcelItem
      ]
      setLabelPackageType(subItem.label)
      const newFirstParcel = {
        ...firstParcel,
        dimension_unit: firstParcel.dimension_unit || DIMENSION_UNIT.IN,
        weight_unit: firstParcel.weight_unit || WEIGHT_UNIT.OZ,
        package_count: firstParcel.package_count || '',
        ...subItem.value,
      }
      await formik.setFieldValue('parcels', {
        ...currentParcels,
        [firstParcelKey]: newFirstParcel,
      })
      await formik.setFieldTouched(name, true)
    }

    return (
      <div className={clsx('dropdown-submenu', {show: visibleSubMenu === label})} key={index}>
        <div
          className={clsx('dropdown-submenu__item item-custom', {
            show: visibleSubMenu === label,
          })}
          onClick={() => handleClickPackage(item, options)}
        >
          {label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
          label === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label
            ? intl.formatMessage({id: label})
            : label}
        </div>
        <div
          className={clsx('dropdown-submenu__inner', {show: visibleSubMenu === label})}
          style={{overflowY: 'auto', maxHeight: '200px'}}
        >
          {!isEmpty(options) &&
            options?.map((subItem) => {
              return (
                <div
                  key={subItem.label}
                  className={`dropdown-submenu__inner__item item-custom ${
                    highLightPackageCarrier(subItem)
                      ? 'text-white bg-primary'
                      : 'text-hover-primary bg-hover-active-primary'
                  }`}
                  onClick={() => handleClickPackageChild(subItem)}
                >
                  {subItem.label}
                </div>
              )
            })}
        </div>
        {OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label === label &&
          visibleSubMenu === label &&
          showAddPackage && (
            <div
              className={`dropdown-submenu__inner__item item-custom`}
              onClick={() => {
                setVisibleMenu(false)
                formik.setFieldValue(name, value)
                setShowModalNewPackage(true)
              }}
            >
              <Button
                className='btn btn-link py-0'
                label={`+ ${intl.formatMessage({id: 'ADD_CUSTOM_PACKAGE'})}`}
              />
            </div>
          )}
      </div>
    )
  }

  return (
    <>
      <CreatePackagesModal
        show={showModalNewPackage}
        handleClose={() => {
          const firstParcelKey: string | number = Object.keys(formik.values.parcels)?.[0]

          formik.setFieldValue(`parcels.${firstParcelKey}.package_type`, '')
          setShowModalNewPackage(false)
        }}
        handleCreatePackagesSuccess={handleCreatePackagesSuccess}
      />
      {textLabel && (
        <label className={`form-label ${labelClassName} ${required ? 'required' : ''}`}>
          {textLabel}
        </label>
      )}
      <div className={clsx('dropdown', className, {'cursor-no-drop': disabled})} ref={storeRef}>
        <div
          className={clsx('dropdown__label dropdown-trigger', {'pe-none': disabled})}
          onClick={handleDropdownClick}
          style={customStyle}
        >
          <div className='dropdown__label__left fw-bold'>
            <div>
              {oneRateUnmatched
                ? placeholder
                : labelPackageType === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
                  labelPackageType === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label
                ? intl.formatMessage({id: labelPackageType})
                : labelPackageType}
            </div>
          </div>
          <div className='dropdown__label__arrow dropdown-trigger__indicator'>
            <span className='dropdown-trigger__separator' />
            <KTSVG
              path='/media/gori/arrows/chevron_down.svg'
              className='dropdown-trigger__chevron'
            />
          </div>
        </div>
        <div
          className={clsx('dropdown-menu dropdown__menu  mw-100', {show: visibleMenu})}
          style={{width: '100%'}}
        >
          {data.map((item, index) => renderMenuItem(item, index))}
        </div>
        {formikTouched && formikErrors && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block text-danger'>
              <span role='alert'>{formikErrors}</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {PackageType}
